import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DELIVERED_STATUS_LABEL } from '@constants';
import { DriverOrder, ProductDriverOrder } from '@models';
import { AlertService, GoogleAnalyticsService } from '@services';
import { equalsIgnoreCase, GetUnitText } from '@utils';
import {
  DeliveryCodeDialogComponent,
  PreviewPhotoDialogComponent,
} from '../dialogs';
import { CameraButtonComponent } from '../camera-button/camera-button.component';

@Component({
  selector: 'digicem-order-driver-card',
  templateUrl: './order-driver-card.component.html',
})
export class OrderDriverCardComponent implements OnInit, OnDestroy {
  @Input() public order!: DriverOrder;
  @ViewChild(CameraButtonComponent) cameraButton!: CameraButtonComponent;
  @Output() public deliveryConfirmed = new EventEmitter<void>();

  protected copied = false;
  protected isDelivered = false;
  protected photoUrl: string | undefined | null = null;

  constructor(
    private alertService: AlertService,
    private dialog: MatDialog,
    private gaService: GoogleAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.isDelivered = equalsIgnoreCase(
      this.order?.spanishStatusName,
      DELIVERED_STATUS_LABEL,
    );
  }

  ngOnDestroy(): void {
    // Limpiar URL de la foto si existe
    if (this.photoUrl) {
      URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = null;
    }
  }

  protected copy(): void {
    const address = this.getAddress();
    if (address) {
      navigator.clipboard
        .writeText(address)
        .then(() => {
          this.copied = true;
          this.alertService.showAlert(
            'La dirección de envío se copió correctamente.',
          );

          setTimeout(() => {
            this.copied = false;
          }, 2000);
        })
        .catch(() => {
          this.alertService.showAlert(
            'Error al copiar la dirección de envío. Inténtalo de nuevo.',
            'critical',
          );
        });
    }
  }

  public onEnterCode(): void {
    this.dialog.open(DeliveryCodeDialogComponent, {
      data: {
        order: this.order!,
        onAccept: () => {
          this.logEvent('enter_code');
          this.showMessageSuccess();
        },
      },
    });
  }

  protected getQuantityProduct(product: ProductDriverOrder): string {
    return (
      product.quantity +
      ' ' +
      GetUnitText(product.measurementUnit, product.quantity <= 1)
    );
  }

  protected getTitleProduct(index: number): string {
    if (this.order.products.length === 1) {
      return 'Producto';
    }
    return 'Producto #' + (index + 1).toString();
  }

  protected getAddress(): string {
    return (
      this.order.addressLine +
      ' ' +
      this.order.addressNumber +
      ', ' +
      this.order.district
    );
  }

  protected onFileSelected(file: File): void {
    const dialogRef = this.dialog.open(PreviewPhotoDialogComponent, {
      data: {
        previewFile: file,
        saleOrderId: this.order.saleOrderId,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.photoUrl = URL.createObjectURL(file);
        this.logEvent('upload_photo');
        this.showMessageSuccess();
      } else if (result === 'take_other_photo') {
        this.cameraButton.openCamera();
      }
    });
  }

  protected showMessageSuccess(): void {
    this.isDelivered = true;
    this.alertService.showAlert(
      'El registro ha sido validado exitosamente. Ganaste 5 puntos. ¡Sigue así!',
      'success',
    );
    this.deliveryConfirmed.emit();
  }

  private logEvent(action: 'enter_code' | 'upload_photo'): void {
    const actionLabels: { [key: string]: string } = {
      enter_code: 'Ingresar código',
      upload_photo: 'Subir foto',
    };
    this.gaService.trackEvent(`${action}_delivery`, {
      event_category: 'SellerDriver',
      event_label: `${actionLabels[action]} del pedido: ${this.order.saleOrderId}`,
      value: this.order.saleOrderId,
      section_title: 'Choferes',
    });
  }
}
